import { type TypedDocumentNode, gql } from '@apollo/client';
import { faDown, faUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { InningHalf } from '@sorare/core/src/__generated__/globalTypes';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { NamedClubAvatar } from '@sorare/core/src/components/club/NamedClubAvatar';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DomesticLeagueRanking } from '@sorare/marketplace/src/shared/game/DomesticLeagueRanking';
import {
  isGameFinished,
  isGameLive,
  isGameStarted,
} from '@sorare/marketplace/src/shared/game/lib';

import { GamesStanding } from 'components/team/GamesStanding';
import { getTeamName } from 'lib/so5';

import { RHEGrid } from '../RHEGrid';
import { ScoresByInning } from '../ScoresByInning';
import type { GameViewHeader_anyGameInterface } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  width: 100%;
  display: flex;
  position: relative;

  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    conic-gradient(
      from 180deg at 50% 50%,
      var(--c-black),
      color-mix(in srgb, var(--left-team-color) 40%, transparent) 90deg,
      var(--c-black) 180deg,
      color-mix(in srgb, var(--right-team-color) 40%, transparent) 270deg,
      var(--c-black)
    );
`;
const Matchup = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  align-items: center;
  justify-items: center;
  gap: var(--unit);
  padding: var(--quadruple-unit) var(--double-unit) var(--intermediate-unit);
  width: 100%;
  overflow: hidden;

  &.reversed {
    > *:first-child {
      order: 2;
    }
    > *:nth-child(3) {
      order: -1;
    }
  }

  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit) var(--quadruple-unit)
      var(--intermediate-unit);
  }
`;
const Scores = styled(Horizontal)`
  color: var(--c-nd-600);
  .reversed & {
    flex-direction: row-reverse;
  }

  .winning {
    font-weight: var(--t-bold);
    color: var(--c-white);
  }
`;
const LiveWrapper = styled(Horizontal)`
  border-radius: var(--half-unit);
  padding: var(--half-unit);
  background-color: rgba(var(--c-rgb-red-400), 0.16);
  color: var(--c-red-300);
`;

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

type Props = {
  game: GameViewHeader_anyGameInterface;
  displayDay?: boolean;
  children?: ReactNode;
  onClose: () => void;
};

export const GameViewHeader = ({ game, children, onClose }: Props) => {
  if (!game.awayTeam || !game.homeTeam) return null;

  const isBaseballGame = isType(game, 'GameOfBaseball');
  const isBasketballGame = isType(game, 'GameOfBasketball');
  const isUsSportsGame = isBaseballGame || isBasketballGame;

  const homeTeam = {
    name: getTeamName(game.homeTeam),
    avatar: <TeamAvatar team={game.homeTeam} />,
  };
  const awayTeam = {
    name: getTeamName(game.awayTeam),
    avatar: <TeamAvatar team={game?.awayTeam} />,
  };

  const live = isGameLive(game.status);
  const finished = isGameFinished(game.status);
  const started = isGameStarted(game.status);
  const isHomeTeamWinning = game.homeScore > game.awayScore;

  const leftTeam = isUsSportsGame ? game.awayTeam : game.homeTeam;
  const rightTeam = isUsSportsGame ? game.homeTeam : game.awayTeam;

  return (
    <Root
      style={
        {
          // 66 in hexa is 40% opacity
          '--left-team-color': `${leftTeam.styleGuideColor}`,
          '--right-team-color': `${rightTeam.styleGuideColor}`,
        } as CSSProperties
      }
    >
      {!!onClose && <StyledCloseButton onClose={onClose} size="medium" />}
      <Matchup className={classNames({ reversed: isUsSportsGame })}>
        <Vertical gap={0.5} center>
          <NamedClubAvatar team={homeTeam} bold Label={LabelL} />
          {!!game.homeTeam && (
            <>
              {isUsSportsGame ? (
                <GamesStanding team={game.homeTeam} color="var(--c-nd-600)" />
              ) : (
                <DomesticLeagueRanking game={game} team={game.homeTeam} />
              )}
            </>
          )}
        </Vertical>
        <Vertical center gap={0.5}>
          <HeadlineXL as="span">
            {live || finished ? (
              <Scores gap={0.5}>
                <span
                  className={classNames({
                    winning: isHomeTeamWinning,
                  })}
                >
                  {game.homeScore}
                </span>
                <span>-</span>
                <span
                  className={classNames({
                    winning: !isHomeTeamWinning,
                  })}
                >
                  {game.awayScore}
                </span>
              </Scores>
            ) : (
              <FormattedDate
                value={game.date}
                hour="numeric"
                minute="numeric"
              />
            )}
            {!!live && (
              <LiveWrapper center>
                <LabelS as="span" bold>
                  <Horizontal gap={0.5}>
                    {isType(game, 'Game') && (
                      <>
                        <LiveDot animate size="sm" />
                        {game.minute}&apos;
                      </>
                    )}
                    {isType(game, 'GameOfBaseball') && (
                      <>
                        <FontAwesomeIcon
                          icon={
                            game.inning.half === InningHalf.TOP ? faUp : faDown
                          }
                          size="sm"
                        />
                        <FormattedMessage
                          {...glossary.ordinal}
                          values={{
                            ordinal: game.inning.number,
                          }}
                        />
                      </>
                    )}
                    {isType(game, 'GameOfBasketball') && (
                      <>
                        <LiveDot animate size="sm" />
                        {game.isHalftime ? (
                          <FormattedMessage
                            id="Game.status.halftime"
                            defaultMessage="Half"
                          />
                        ) : (
                          <FormattedMessage
                            {...glossary.ordinal}
                            values={{ ordinal: game.quarter }}
                          />
                        )}
                      </>
                    )}
                  </Horizontal>
                </LabelS>
              </LiveWrapper>
            )}
          </HeadlineXL>
        </Vertical>
        <Vertical gap={0.5} center>
          <NamedClubAvatar team={awayTeam} bold Label={LabelL} />
          {!!game.awayTeam && (
            <>
              {isUsSportsGame ? (
                <GamesStanding team={game.awayTeam} color="var(--c-nd-600)" />
              ) : (
                <DomesticLeagueRanking game={game} team={game.awayTeam} />
              )}
            </>
          )}
        </Vertical>
      </Matchup>
      {!!isBaseballGame && !!started && (
        <Horizontal center gap={4}>
          <ScoresByInning game={game} />
          <RHEGrid game={game} />
        </Horizontal>
      )}
      {children}
    </Root>
  );
};

GameViewHeader.fragments = {
  anyGameInterface: gql`
    fragment GameViewHeader_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      date
      awayScore
      homeScore
      awayTeam {
        slug
        styleGuideColor
        ...TeamAvatar_team
        ...getTeamName_team
        ...GamesStanding_TeamInterface
        ...DomesticLeagueRanking_teamInterface
      }
      homeTeam {
        slug
        styleGuideColor
        ...TeamAvatar_team
        ...getTeamName_team
        ...GamesStanding_TeamInterface
        ...DomesticLeagueRanking_teamInterface
      }
      ... on Game {
        id
        minute
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
      ... on GameOfBaseball {
        id
        inning {
          half
          number
        }
        ...RHEGrid_GameOfBaseball
      }
      ...DomesticLeagueRanking_anyGameInterface
      ...ScoresByInning_GameOfBaseball
    }
    ${TeamAvatar.fragments.team}
    ${getTeamName.fragments.team}
    ${GamesStanding.fragments.TeamInterface}
    ${DomesticLeagueRanking.fragments.anyGameInterface}
    ${DomesticLeagueRanking.fragments.teamInterface}
    ${ScoresByInning.fragments.GameOfBaseball}
    ${RHEGrid.fragments.GameOfBaseball}
  ` as TypedDocumentNode<GameViewHeader_anyGameInterface>,
};
